import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { adminAnalyticsService } from "../../../../services/adminAnalyticsService";
import AlertRuleForm from "./AlertRuleForm";
import AlertDeliveryLogsPanel from "./AlertDeliveryLogsPanel";
import DataTable from "../DataTable";
import { formatDateTime, normalizeListPayload } from "../analyticsUtils";
import { SectionError, SectionLoading } from "../SectionState";

const SCHEDULE_SUMMARY = {
  daily: "Send once eligible, then at most once per day",
  weekly: "Send once eligible, then at most once every 7 days",
};

const RULE_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "rule_type", label: "Type" },
  {
    key: "enabled",
    label: "Enabled",
    render: (value) => (value ? "Yes" : "No"),
  },
  { key: "threshold_days", label: "Threshold Days" },
  {
    key: "schedule",
    label: "Resend Cadence",
    render: (value) => SCHEDULE_SUMMARY[value] || value || "--",
  },
  {
    key: "max_deliveries_per_user",
    label: "Max Deliveries",
    render: (value) => (value === null || value === undefined ? "No cap" : value),
  },
  {
    key: "last_run_at",
    label: "Last Run At",
    render: (value) => formatDateTime(value),
  },
  {
    key: "created_at",
    label: "Created At",
    render: (value) => formatDateTime(value),
  },
  {
    key: "updated_at",
    label: "Updated At",
    render: (value) => formatDateTime(value),
  },
];

const getRuleId = (rule) => rule?.rule_id ?? rule?.id;

const DetailItem = ({ label, value, monospace = false }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
      {label}
    </div>
    <div className={`mt-1 text-sm text-slate-800 ${monospace ? "font-mono break-all" : ""}`}>
      {value || "--"}
    </div>
  </div>
);

const AlertsTab = () => {
  const [rulesState, setRulesState] = useState({
    items: [],
    total: null,
    loading: false,
    error: "",
  });

  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [runEnabledLoading, setRunEnabledLoading] = useState(false);
  const [runningByRule, setRunningByRule] = useState({});
  const [toggleByRule, setToggleByRule] = useState({});

  const selectedRule =
    rulesState.items.find((rule) => String(getRuleId(rule)) === String(selectedRuleId)) ||
    null;

  const loadRules = useCallback(async () => {
    setRulesState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const payload = await adminAnalyticsService.getAlertRules();
      const normalized = normalizeListPayload(payload);
      setRulesState({
        ...normalized,
        loading: false,
        error: "",
      });
      if (!selectedRuleId && normalized.items.length > 0) {
        setSelectedRuleId(getRuleId(normalized.items[0]));
      }
    } catch (err) {
      setRulesState((prev) => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.detail || "Failed to load alert rules.",
      }));
    }
  }, [selectedRuleId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleSubmitForm = async (payload) => {
    try {
      setSubmittingForm(true);
      const ruleId = getRuleId(editingRule);
      if (ruleId) {
        await adminAnalyticsService.updateAlertRule(ruleId, payload);
        toast.success("Alert rule updated.");
      } else {
        await adminAnalyticsService.createAlertRule(payload);
        toast.success("Alert rule created.");
      }
      setEditingRule(null);
      await loadRules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save alert rule.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleRunNow = async (rule) => {
    const ruleId = getRuleId(rule);
    if (!ruleId) return;

    setRunningByRule((prev) => ({ ...prev, [ruleId]: true }));
    try {
      await adminAnalyticsService.runAlertRule(ruleId);
      toast.success("Alert run triggered.");
      await loadRules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to run alert.");
    } finally {
      setRunningByRule((prev) => ({ ...prev, [ruleId]: false }));
    }
  };

  const handleToggleEnabled = async (rule) => {
    const ruleId = getRuleId(rule);
    if (!ruleId) return;

    setToggleByRule((prev) => ({ ...prev, [ruleId]: true }));
    try {
      await adminAnalyticsService.updateAlertRule(ruleId, {
        enabled: !Boolean(rule.enabled),
      });
      toast.success("Alert rule updated.");
      await loadRules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update alert status.");
    } finally {
      setToggleByRule((prev) => ({ ...prev, [ruleId]: false }));
    }
  };

  const handleRunEnabled = async () => {
    setRunEnabledLoading(true);
    try {
      await adminAnalyticsService.runEnabledAlertRules();
      toast.success("Enabled alert rules run triggered.");
      await loadRules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to run enabled alerts.");
    } finally {
      setRunEnabledLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Alert Rules</h3>
            <p className="mt-1 text-sm text-slate-500">
              Manage automation rules and trigger alert runs when needed. Schedule refers to resend cadence after eligibility, not first-send delay.
            </p>
          </div>
          <button
            type="button"
            onClick={handleRunEnabled}
            disabled={runEnabledLoading}
            className="rounded-xl bg-[#54B0AF] px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {runEnabledLoading ? "Running..." : "Run Enabled"}
          </button>
        </div>

        {rulesState.loading && <SectionLoading text="Loading alert rules..." />}
        {rulesState.error && <SectionError message={rulesState.error} />}

        {!rulesState.loading && !rulesState.error && (
          <div className="space-y-3">
            <DataTable
              rows={rulesState.items}
              columns={[
                ...RULE_COLUMNS,
                {
                  key: "__actions",
                  label: "Actions",
                  render: (_, row) => {
                    const ruleId = getRuleId(row);
                    const isRunning = runningByRule[ruleId];
                    const isToggling = toggleByRule[ruleId];
                    const isSelected = String(selectedRuleId) === String(ruleId);

                    return (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700"
                          onClick={() => {
                            setEditingRule(row);
                            setSelectedRuleId(ruleId);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-50"
                          onClick={() => handleRunNow(row)}
                          disabled={Boolean(isRunning)}
                        >
                          {isRunning ? "Running..." : "Run"}
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-50"
                          onClick={() => handleToggleEnabled(row)}
                          disabled={Boolean(isToggling)}
                        >
                          {isToggling
                            ? "Saving..."
                            : row.enabled
                            ? "Disable"
                            : "Enable"}
                        </button>
                        <button
                          type="button"
                          className={`rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium ${
                            isSelected ? "bg-slate-100 text-slate-900" : "text-slate-700"
                          }`}
                          onClick={() => setSelectedRuleId(ruleId)}
                        >
                          Deliveries
                        </button>
                      </div>
                    );
                  },
                },
              ]}
              emptyText="No alert rules found."
            />
          </div>
        )}
      </div>

      <AlertRuleForm
        selectedRule={editingRule}
        loading={submittingForm}
        onSubmit={handleSubmitForm}
        onCancel={() => setEditingRule(null)}
      />

      {selectedRule && (
        <div className="space-y-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Selected Rule Details</h3>
            <p className="mt-1 text-sm text-slate-500">
              First send happens as soon as the threshold is met. The schedule below only controls resend timing while the user still qualifies.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <DetailItem label="Name" value={selectedRule.name} />
            <DetailItem label="Rule Type" value={selectedRule.rule_type} />
            <DetailItem
              label="Threshold"
              value={
                selectedRule.threshold_days
                  ? `${selectedRule.threshold_days} day${Number(selectedRule.threshold_days) === 1 ? "" : "s"} before first eligibility`
                  : "--"
              }
            />
            <DetailItem
              label="Resend Cadence"
              value={SCHEDULE_SUMMARY[selectedRule.schedule] || selectedRule.schedule}
            />
            <DetailItem
              label="Max Deliveries Cap"
              value={
                selectedRule.max_deliveries_per_user === null ||
                selectedRule.max_deliveries_per_user === undefined
                  ? "No cap"
                  : `${selectedRule.max_deliveries_per_user} total successful sends per user`
              }
            />
            <DetailItem label="Enabled" value={selectedRule.enabled ? "Yes" : "No"} />
            <DetailItem label="CTA Label" value={selectedRule.cta_label} />
            <DetailItem label="CTA URL" value={selectedRule.cta_url} monospace />
            <DetailItem label="Last Run At" value={formatDateTime(selectedRule.last_run_at)} />
          </div>
        </div>
      )}

      <AlertDeliveryLogsPanel ruleId={selectedRuleId} />
    </div>
  );
};

export default AlertsTab;
